import { normalizeOnboardingWebsiteUrl, ONBOARDING_WEBSITE_URL_ERROR } from '@overlayqa/shared';
import { useState, useEffect, useRef } from 'react';
import { cn } from '@/lib/utils';
import type { OnboardingCompleteRequest } from '@overlayqa/shared';
import { StepWelcome } from './StepWelcome';
import { StepAboutYou } from './StepAboutYou';
import { StepGoal } from './StepGoal';
import { StepProject } from './StepProject';
import { resolveProjectName } from './StepProject';
import type { UrlType } from './StepProject';
import { StepInviteTeam } from './StepInviteTeam';
import type { InviteSeatInfo } from './StepInviteTeam';
import { StepJoinTeam } from './StepJoinTeam';
import { Spinner } from '@/components/ui/Spinner';
import type { JoinCandidatesResponse } from '@/lib/api/teamJoinRequests';
import { isLocalUrl } from '@/lib/utils';
import { normalizeInviteEmails } from '@/lib/inviteEmail';
import { log } from '@/lib/logger';
import { capture } from '@/lib/analytics';
import { onboardingWebsiteError } from '@/lib/onboardingWebsite';
import { clearFreeToolSite, readFreeToolSite } from '@/lib/freeToolSite';
import { toUserFacingMessage } from '@overlayqa/shared';

interface OnboardingWizardProps {
  onSave: (data: OnboardingCompleteRequest) => Promise<{ projectId?: string; projectName?: string; projectSlug?: string; teamId?: string }>;
  onFinish: (urlInfo: { url: string; urlType: UrlType; projectId?: string; projectName?: string; projectSlug?: string; teamId?: string }) => Promise<void>;
  /** Asks the server whether the weekly scan can reach a URL; the project step shows the answer. */
  onScanPreflight?: (url: string) => Promise<{ scannable: boolean; reason: string }>;
  /**
   * Step 5: send the typed addresses into the team the save resolved. Runs
   * AFTER onSave and BEFORE onFinish, and never blocks the routing: a rejection
   * is logged and reported, the user still lands on their review. Optional so
   * the page can leave the step read-only in tests.
   */
  onInvite?: (teamId: string, emails: string[]) => Promise<{ sent: number; failed: number }>;
  /**
   * Seats the team can still fill (from /api/billing/plan). `invitableSeats: 0`
   * hides step 5 entirely: a step that can only say "you can't" is score-keeping.
   * Undefined or null (billing not loaded) still shows the step, because the
   * server enforces the cap on every invite anyway; the seat sentence is then
   * omitted rather than guessed.
   */
  seats?: InviteSeatInfo | null;
  /**
   * Step 5's JOIN state (spec 2026-09-10): the owner-matched teams the page
   * read for this signup. One or more candidates opens step 5 as the offer to
   * join instead of the invite form; empty (also what the server answers while
   * its flag is off) renders the wizard exactly as before.
   */
  joinCandidates?: JoinCandidatesResponse | null;
  /**
   * True while the page's candidates read is still in flight. Step 5 then
   * holds a loading state instead of drawing the invite form and swapping it
   * for the offer when the answer lands: measured on the 2026-09-10 mini run,
   * the swap came 784 ms after step 5 rendered (the read starts only after
   * the session bootstraps, and a fast walk reaches step 5 first).
   */
  joinCandidatesPending?: boolean;
  /**
   * "Request to join": the page POSTs the request and reports it. Resolves on
   * success; rejects with the server's sentence, which the offer shows inline.
   */
  onRequestJoin?: (teamId: string) => Promise<void>;
  /** Invited slim wizard (Fix 5): role + goal only, no company/project steps. */
  invited?: boolean;
  invitedTeamName?: string | null;
  /**
   * Called after a successful invited-mode save. Awaited: the page refreshes
   * the session and then navigates to /dashboard, and the goal step stays
   * locked (and the invited signal set) until it settles.
   */
  onInvitedFinish?: () => void | Promise<void>;
}

const TOTAL_STEPS = 5;
/** The owner wizard without step 5 (a plan with no invitable seat). */
const OWNER_STEPS_WITHOUT_INVITE = 4;
const INVITED_TOTAL_STEPS = 2;
/** What the save sends when step 1 was left blank; also what step 5's helper names. */
const DEFAULT_COMPANY_NAME = 'My Company';
export const STORAGE_KEY = 'overlayqa_onboarding';
// Written by InvitePage on a successful accept (value = the invited teamId) so
// a mid-wizard refresh keeps the slim invited flow without the ?invited=1 param.
export const INVITED_STORAGE_KEY = 'overlayqa_onboarding_invited';

type JoinChoice = 'offer' | 'own';
type JoinRequested = { teamId: string; teamName: string } | null;

function loadSavedState(): { step: number; companyName: string; role: string; goal: string; projectName: string; projectUrl: string; urlType: UrlType; scheduledScanOptIn: boolean; inviteEmails: string[]; joinChoice: JoinChoice; joinRequested: JoinRequested } {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (raw) {
      const parsed = JSON.parse(raw);
      log.info('[OnboardingWizard] restored state from sessionStorage', { parsed });
      // Retired Notion/Shopify selections return to the site step. SaaS Demo
      // remains available and keeps its saved selection across a refresh.
      const retiredDemo = parsed.urlType === 'notion' || parsed.urlType === 'shopify';
      return {
        step: Math.min(Math.max(parsed.step ?? 1, 1), retiredDemo ? 4 : TOTAL_STEPS),
        companyName: parsed.companyName ?? '',
        role: parsed.role ?? '',
        goal: parsed.goal ?? '',
        projectName: retiredDemo && ['Notion', 'Shopify'].includes(parsed.projectName) ? '' : parsed.projectName ?? '',
        projectUrl: retiredDemo ? '' : parsed.projectUrl ?? '',
        urlType: retiredDemo ? 'own' : parsed.urlType ?? 'own',
        // Restored as checked unless the user explicitly unchecked it before
        // the redirect, matching the checkbox's own default.
        scheduledScanOptIn: parsed.scheduledScanOptIn !== false,
        joinChoice: parsed.joinChoice === 'own' ? 'own' : 'offer',
        joinRequested:
          parsed.joinRequested && typeof parsed.joinRequested.teamId === 'string' && typeof parsed.joinRequested.teamName === 'string'
            ? { teamId: parsed.joinRequested.teamId, teamName: parsed.joinRequested.teamName }
            : null,
        inviteEmails: Array.isArray(parsed.inviteEmails) && parsed.inviteEmails.length > 0
          ? parsed.inviteEmails.map((e: unknown) => (typeof e === 'string' ? e : ''))
          : [''],
      };
    }
  } catch {
    // ignore corrupt storage
  }
  // A visitor who left a free scan-tool review for an account starts with the site they audited (lib/freeToolSite.ts).
  return { step: 1, companyName: '', role: '', goal: '', projectName: '', projectUrl: readFreeToolSite() ?? '', urlType: 'own', scheduledScanOptIn: true, inviteEmails: [''], joinChoice: 'offer', joinRequested: null };
}

function clearSavedState() {
  clearFreeToolSite();
  try {
    sessionStorage.removeItem(STORAGE_KEY);
  } catch {
    // ignore
  }
}

function clearInvitedSignal() {
  try {
    sessionStorage.removeItem(INVITED_STORAGE_KEY);
  } catch {
    // ignore
  }
}

export function OnboardingWizard({ onSave, onFinish,
  onScanPreflight, onInvite, seats, joinCandidates, joinCandidatesPending = false, onRequestJoin, invited = false, invitedTeamName, onInvitedFinish }: OnboardingWizardProps) {
  const [saved] = useState(loadSavedState);
  // Step 5 JOIN state (spec 2026-09-10). The offer stands while the page has
  // candidates and the user has not chosen their own workspace; it does not
  // depend on the user's OWN seats, because they are joining another team.
  const [joinChoice, setJoinChoice] = useState<JoinChoice>(saved.joinChoice);
  const [joinRequested, setJoinRequested] = useState<JoinRequested>(saved.joinRequested);
  const [isRequestingJoin, setIsRequestingJoin] = useState(false);
  const [joinRequestError, setJoinRequestError] = useState<string | null>(null);
  const candidates = joinCandidates?.candidates ?? [];
  const hasJoinOffer = !invited && joinChoice !== 'own' && candidates.length > 0;
  // While the read that decides between the offer and the invite form is
  // still in flight, step 5 shows neither: drawing the invite form first and
  // swapping it for the offer when the answer lands is a flash the user reads
  // as the page changing its mind (see joinCandidatesPending on the props).
  const joinOfferPending = !invited && joinChoice !== 'own' && candidates.length === 0 && joinCandidatesPending;
  // A candidate the server already marks requested (a refresh after the ask)
  // opens straight onto the sent state, so nobody asks twice.
  const serverRequested = candidates.find((c) => c.requested);
  const effectiveJoinRequested: JoinRequested =
    joinRequested ?? (serverRequested ? { teamId: serverRequested.teamId, teamName: serverRequested.name } : null);
  // The invite form is offered unless billing says there is no seat to fill.
  const inviteStepAvailable = !invited && seats?.invitableSeats !== 0;
  const step5Available = hasJoinOffer || inviteStepAvailable;
  const totalSteps = invited ? INVITED_TOTAL_STEPS : step5Available ? TOTAL_STEPS : OWNER_STEPS_WITHOUT_INVITE;
  // Clamp a restored owner-wizard step so invited mode never strands past its last step.
  const [step, setStep] = useState(() => Math.min(saved.step, totalSteps));
  // Billing can remove the invite step after the user has already reached it.
  // Preserve the draft and return to its last available step instead of a blank card.
  useEffect(() => {
    if (step > totalSteps) setStep(totalSteps);
  }, [step, totalSteps]);
  const [companyName, setCompanyName] = useState(saved.companyName);
  const [role, setRole] = useState(saved.role);
  const [goal, setGoal] = useState(saved.goal);
  const [projectName, setProjectName] = useState(saved.projectName);
  const [projectUrl, setProjectUrl] = useState(saved.projectUrl);
  const [urlType, setUrlType] = useState<UrlType>(saved.urlType);
  const [scheduledScanOptIn, setScheduledScanOptIn] = useState(saved.scheduledScanOptIn);
  const [inviteEmails, setInviteEmails] = useState<string[]>(saved.inviteEmails);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Same-tick re-entry guard for the invited finish (see handleInvitedFinish).
  const invitedFinishInFlightRef = useRef(false);

  // Persist wizard state to sessionStorage so Clerk checkout redirects don't lose progress
  useEffect(() => {
    try {
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify({ step, companyName, role, goal, projectName, projectUrl, urlType, scheduledScanOptIn, inviteEmails, joinChoice, joinRequested }));
      log.info('[OnboardingWizard] saved state to sessionStorage', { step, companyName, role, goal, projectName, projectUrl, urlType, scheduledScanOptIn, inviteEmails, joinChoice, joinRequested });
    } catch {
      // ignore quota errors
    }
  }, [step, companyName, role, goal, projectName, projectUrl, urlType, scheduledScanOptIn, inviteEmails, joinChoice, joinRequested]);

  log.info('[OnboardingWizard] render', { step, companyName, role, goal, isSubmitting, error });

  function goNext() {
    log.info('[OnboardingWizard] goNext from step', { step });
    setError(null);
    // Step-level funnel: started/completed alone can't locate WHERE the
    // wizard loses people (2026-06-11 instrumentation audit).
    if (step < totalSteps) {
      capture('dashboard_onboarding_step_completed', { from_step: step, to_step: step + 1 });
    }
    setStep((s) => Math.min(s + 1, totalSteps));
  }

  function goBack() {
    log.info('[OnboardingWizard] goBack from step', { step });
    setError(null);
    setStep((s) => Math.max(s - 1, 1));
  }

  /**
   * Finish, from whichever step is last (step 5, or step 4 when there is no
   * invitable seat). Order is save -> invite -> route: the invites need the
   * team the save resolved (it renames the team to the step-1 company name and
   * returns its id), and an invite failure must never cost the user their
   * review, so the invite leg catches everything and the routing runs anyway.
   */
  async function handleFinish(options: { skipInvites?: boolean } = {}) {
    log.info('[OnboardingWizard] handleFinish called', { skipInvites: Boolean(options.skipInvites) });
    setError(null);
    setIsSubmitting(true);

    try {
      // Always save explicitly — never rely on auto-save completing first.
      // The endpoint is idempotent so double-saves are harmless.
      const normalizedUrl = normalizeOnboardingWebsiteUrl(projectUrl);
      if (!normalizedUrl) {
        setError(projectUrl.trim() ? ONBOARDING_WEBSITE_URL_ERROR : 'Please enter a website URL to continue.');
        setStep(4);
        return;
      }

      const data: OnboardingCompleteRequest = {
        companyName: companyName.trim() || DEFAULT_COMPANY_NAME,
        role: role || 'other',
        goal: goal || 'other',
        // The step-4 name field ghosts the URL-derived name in its placeholder
        // rather than its value, so a blank field means "call it what the ghost
        // said". resolveProjectName keeps that promise; sending blank would let
        // the server name it the full hostname instead (onboarding.ts:228).
        projectName: resolveProjectName(projectName, projectUrl),
        projectUrl: normalizedUrl,
        // Sent every time, never conditionally: the server reads absent as
        // "enrol" so an old client keeps today's behaviour, which means a
        // decline that is merely omitted would silently become a yes.
        scheduledScanOptIn,
      };
      // A restored invite step can bypass StepProject's Continue gate. Refuse
      // before saving or sending invitations, and return to the field to fix it.
      if (onboardingWebsiteError(normalizedUrl)) {
        setStep(4);
        return;
      }

      log.info('[OnboardingWizard] saving onboarding data', { data });
      const saveResult = await onSave(data);
      log.info('[OnboardingWizard] save succeeded', { projectId: saveResult.projectId, projectName: saveResult.projectName, teamId: saveResult.teamId });
      await sendInvites(saveResult.teamId, Boolean(options.skipInvites));
      clearSavedState();
      await onFinish({ url: normalizedUrl, urlType, projectId: saveResult.projectId, projectName: saveResult.projectName, projectSlug: saveResult.projectSlug });
      log.info('[OnboardingWizard] finish succeeded');
    } catch (err) {
      // The generic fetchApi placeholder, an empty message and the browser's
      // own network text all fall back; a curated server sentence passes through.
      const message = toUserFacingMessage(err, 'Something went wrong. Please check your inputs and try again.');
      log.error('[OnboardingWizard] finish failed', { err });
      capture('onboarding_finish_failed', {
        reason: err instanceof Error ? err.name : 'unknown',
        message: (err instanceof Error ? err.message : '').slice(0, 200),
        step,
      });
      setError(message);
    } finally {
      setIsSubmitting(false);
    }
  }

  /**
   * The invite leg of Finish. Fire-and-proceed: every outcome is logged and
   * reported, none is thrown, so the caller's routing always runs. Skipped
   * outright when the user pressed Skip for now, typed nothing, or the page
   * wired no onInvite.
   */
  async function sendInvites(teamId: string | undefined, skipInvites: boolean) {
    if (skipInvites || !onInvite) return;
    const emails = normalizeInviteEmails(inviteEmails);
    if (emails.length === 0) return;
    if (!teamId) {
      // The save answered without a team id (an older server). Nothing to
      // invite into; say so loudly rather than inviting into the wrong team.
      log.error('[OnboardingWizard] invites skipped: save returned no teamId', { count: emails.length });
      capture('onboarding_invites_failed', { count: emails.length, reason: 'no_team_id' });
      return;
    }
    try {
      const outcome = await onInvite(teamId, emails);
      log.info('[OnboardingWizard] invites sent', { teamId, ...outcome });
    } catch (err) {
      log.error('[OnboardingWizard] invite batch failed', { err });
      capture('onboarding_invites_failed', {
        count: emails.length,
        reason: err instanceof Error ? err.name : 'unknown',
      });
    }
  }

  /**
   * "Request to join" (spec 2026-09-10). The page POSTs at once, because the
   * sent copy is in the past tense and must never be shown before a row
   * exists. A refusal keeps the offer and shows the server's sentence.
   */
  async function handleRequestJoin(teamId: string) {
    const team = candidates.find((c) => c.teamId === teamId);
    if (!team || !onRequestJoin) {
      log.error('[OnboardingWizard] request to join ignored', { teamId, hasHandler: Boolean(onRequestJoin) });
      return;
    }
    setJoinRequestError(null);
    setIsRequestingJoin(true);
    try {
      await onRequestJoin(teamId);
      log.info('[OnboardingWizard] join request sent', { teamId });
      setJoinRequested({ teamId, teamName: team.name });
    } catch (err) {
      log.warn('[OnboardingWizard] join request refused', { teamId, message: err instanceof Error ? err.message : '' });
      setJoinRequestError(toUserFacingMessage(err, 'Something went wrong. Please try again.'));
    } finally {
      setIsRequestingJoin(false);
    }
  }

  /**
   * "Start my own workspace": the invite form takes step 5 for the rest of the
   * session. On a plan with no invitable seat there is no form to show, so the
   * click finishes directly, exactly what step 4's Finish does for that plan.
   */
  function handleStartOwn() {
    log.info('[OnboardingWizard] join offer declined', { candidates: candidates.length, inviteStepAvailable });
    capture('onboarding_join_declined', { candidates: candidates.length });
    if (inviteStepAvailable) {
      setJoinChoice('own');
      return;
    }
    void handleFinish({ skipInvites: true });
  }

  function handleSkipInvites() {
    const typed = inviteEmails.filter((e) => e.trim() !== '').length;
    log.info('[OnboardingWizard] invite step skipped', { typed });
    capture('onboarding_invite_skipped', { typed_emails: typed });
    void handleFinish({ skipInvites: true });
  }

  // Invited slim wizard finish (Fix 5): the goal selection is the last step,
  // so it posts role/goal with mode 'invited' and hands navigation back to
  // the page. No company name, no project, no URL.
  async function handleInvitedFinish(selectedGoal: string) {
    // Re-entry guard: the goal cards fire this directly, so a second click
    // before React re-renders with the disabled state would double-POST
    // /onboarding/complete. The ref closes the same-tick race the
    // isSubmitting state can't.
    if (invitedFinishInFlightRef.current) {
      log.info('[OnboardingWizard] handleInvitedFinish ignored (already in flight)');
      return;
    }
    invitedFinishInFlightRef.current = true;
    log.info('[OnboardingWizard] handleInvitedFinish called');
    setError(null);
    setIsSubmitting(true);

    try {
      const data: OnboardingCompleteRequest = {
        role: role || 'other',
        goal: selectedGoal || 'other',
        mode: 'invited',
      };
      log.info('[OnboardingWizard] saving invited onboarding data', { data });
      await onSave(data);
      clearSavedState();
      // Cleared AFTER the finish settles: the page reads this signal to stay in
      // invited mode, and clearing it first flipped the page to the owner
      // wizard while the session refresh was still in flight.
      await onInvitedFinish?.();
      clearInvitedSignal();
      log.info('[OnboardingWizard] invited finish succeeded');
    } catch (err) {
      const message = toUserFacingMessage(err, 'Something went wrong. Please check your inputs and try again.');
      log.error('[OnboardingWizard] invited finish failed', { err });
      capture('onboarding_finish_failed', {
        reason: err instanceof Error ? err.name : 'unknown',
        message: (err instanceof Error ? err.message : '').slice(0, 200),
        step,
      });
      setError(message);
    } finally {
      invitedFinishInFlightRef.current = false;
      setIsSubmitting(false);
    }
  }

  return (
    <div className={cn('w-full mx-auto transition-all duration-300 max-w-lg')}>
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        {/* Progress bar */}
        <div className="px-8 pt-8 pb-2">
          <div className="flex gap-2">
            {Array.from({ length: totalSteps }, (_, i) => (
              <div
                key={i}
                className={cn(
                  'h-1.5 flex-1 rounded-full transition-colors duration-200',
                  i < step ? 'bg-primary-500' : 'bg-gray-200'
                )}
              />
            ))}
          </div>
        </div>

        {/* Error message */}
        {error && (
          <div className="mx-8 mt-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
            {error}
          </div>
        )}

        {/* Step content */}
        <div className="px-8 py-8">
          {invited ? (
            <>
              {step === 1 && (
                <StepAboutYou
                  role={role}
                  heading={`You've joined ${invitedTeamName || 'the team'}`}
                  onSelect={(r) => {
                    setRole(r);
                    goNext();
                  }}
                />
              )}
              {step === 2 && (
                <StepGoal
                  goal={goal}
                  onSelect={(g) => {
                    setGoal(g);
                    void handleInvitedFinish(g);
                  }}
                  onBack={goBack}
                  isSubmitting={isSubmitting}
                />
              )}
            </>
          ) : (
            <>
              {step === 1 && (
                <StepWelcome
                  companyName={companyName}
                  onChange={setCompanyName}
                  onNext={goNext}
                />
              )}
              {step === 2 && (
                <StepAboutYou
                  role={role}
                  onSelect={(r) => {
                    setRole(r);
                    goNext();
                  }}
                  onBack={goBack}
                />
              )}
              {step === 3 && (
                <StepGoal
                  goal={goal}
                  onSelect={(g) => {
                    setGoal(g);
                    goNext();
                  }}
                  onBack={goBack}
                />
              )}
              {step === 4 && (
                <StepProject
                  projectName={projectName}
                  projectUrl={projectUrl}
                  onChangeName={setProjectName}
                  onChangeUrl={setProjectUrl}
                  onUrlTypeChange={setUrlType}
                  scheduledScanOptIn={scheduledScanOptIn}
                  onScheduledScanOptInChange={setScheduledScanOptIn}
                  onScanPreflight={onScanPreflight}
                  isLastStep={!step5Available}
                  onFinish={step5Available ? goNext : () => void handleFinish()}
                  onBack={goBack}
                  isSubmitting={isSubmitting}
                />
              )}
              {step === 5 && hasJoinOffer && (
                <StepJoinTeam
                  domain={joinCandidates?.domain ?? ''}
                  candidates={candidates}
                  requested={effectiveJoinRequested}
                  isRequesting={isRequestingJoin}
                  requestError={joinRequestError}
                  isLocalEntry={projectUrl.trim() !== '' && isLocalUrl(projectUrl.trim())}
                  onRequest={(teamId) => void handleRequestJoin(teamId)}
                  onStartOwn={handleStartOwn}
                  onFinish={() => void handleFinish({ skipInvites: true })}
                  onBack={goBack}
                  isSubmitting={isSubmitting}
                />
              )}
              {step === 5 && joinOfferPending && (
                <div className="flex justify-center py-12" aria-busy="true">
                  <Spinner size="lg" />
                </div>
              )}
              {step === 5 && !hasJoinOffer && !joinOfferPending && inviteStepAvailable && (
                <StepInviteTeam
                  emails={inviteEmails}
                  onChangeEmails={setInviteEmails}
                  teamName={companyName.trim() || DEFAULT_COMPANY_NAME}
                  goal={goal}
                  seats={seats ?? null}
                  isLocalEntry={projectUrl.trim() !== '' && isLocalUrl(projectUrl.trim())}
                  onFinish={() => void handleFinish()}
                  onSkip={handleSkipInvites}
                  onBack={goBack}
                  isSubmitting={isSubmitting}
                />
              )}
            </>
          )}
        </div>

      </div>
    </div>
  );
}
